"use client";

import React, { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Header from "./Header";
import { useAuth } from "../hooks/useAuth";
import theme from "../theme";

const drawerWidth = 200;

const NAV_SECTION_ORDER = [
  "Dashboard",
  "Access Control",
  "Users",
  "Roles",
  "Permissions",
  "Hierarchy",
  "Tenant Data Sync",
];

type NavItem = {
  label: string;
  href: string;
  description?: string;
};

type NavSection = {
  label: string;
  href: string;
  icon: React.ReactNode;
  items: NavItem[];
};

const NAV_SECTIONS: NavSection[] = [
  {
    label: "Dashboard",
    href: "/dashboard",
    icon: <DashboardOutlinedIcon fontSize="small" />,
    items: [
      {
        label: "Overview",
        href: "/dashboard",
        description: "Monitor IAM activity and health",
      },
      {
        label: "Ecosystem Users",
        href: "/dashboard/ecosystem-users",
        description: "Users and invitations in one grid",
      },
      {
        label: "Inactive Report",
        href: "/inactive-report",
        description: "Users by inactivity status",
      },
      {
        label: "API Key Activity",
        href: "/dashboard/api-keys",
        description: "Usage and security activity for API keys",
      },
      {
        label: "User Activity Reports",
        href: "/dashboard/reports/user-activity",
        description: "Generate and export user login reports",
      },
      {
        label: "Inactivity Reviews",
        href: "/dashboard/inactivity-reviews",
        description: "Approve or reject inactivity candidates",
      },
    ],
  },
  {
    label: "Users",
    href: "/users",
    icon: <PeopleAltOutlinedIcon fontSize="small" />,
    items: [
      {
        label: "Directory",
        href: "/users",
        description: "View and search user accounts",
      },
      {
        label: "Create & Invite",
        href: "/users/invite",
        description: "Onboard new collaborators",
      },
      {
        label: "API Keys",
        href: "/users/api-keys",
        description: "Create and manage service-account API keys",
      },
    ],
  },
  {
    label: "Hierarchy",
    href: "/hierarchy",
    icon: <AccountTreeIcon fontSize="small" />,
    items: [
      {
        label: "Tree View",
        href: "/hierarchy",
        description: "Visualize organizational structure",
      },
    ],
  },
  {
    label: "Tenant Data Sync",
    href: "/data-sync",
    icon: <SyncAltOutlinedIcon fontSize="small" />,
    items: [
      {
        label: "Tenant Setup",
        href: "/data-sync/tenants",
        description: "Register external tenant databases",
      },
      {
        label: "Enterprise",
        href: "/data-sync/enterprise",
        description: "Edit source records managed by tenant data sync",
      },
      {
        label: "Relationships",
        href: "/data-sync",
        description: "Configure source-to-tenant sync relationships",
      },
      {
        label: "Scheduler",
        href: "/data-sync/scheduler",
        description: "Run schedules and review history",
      },
    ],
  },
  {
    label: "Roles",
    href: "/roles",
    icon: <ShieldOutlinedIcon fontSize="small" />,
    items: [
      {
        label: "Role Catalog",
        href: "/roles",
        description: "Browse platform and custom roles",
      },
    ],
  },
  // Note: Permission management functionality remains available within the Roles
  // section (role-level permission assignment). The standalone Permissions
  // section has been removed from the navigation to reduce menu clutter.
  {
    label: "Access Control",
    href: "/access",
    icon: <HubOutlinedIcon fontSize="small" />,
    items: [
      {
        label: "Entity directory",
        href: "/access/entities",
        description: "Assign entity-scoped access",
      },
      {
        label: "Role directory",
        href: "/access/roles",
        description: "View role-scoped access",
      },
      {
        label: "Audit activity",
        href: "/access/audit",
        description: "Investigate user and platform activity",
      },
      {
        label: "Session control",
        href: "/access/sessions",
        description: "Review and revoke active sessions",
      },
    ],
  },
];

type DrawerContentProps = {
  pathname: string;
  onNavigate?: () => void;
};

function DrawerContent({
  pathname,
  onNavigate,
  isGlobalAdmin,
  canViewPermissionsModule,
  canViewDashboard,
  canViewInactiveUsers,
  perms,
}: DrawerContentProps & {
  isGlobalAdmin?: boolean;
  canViewPermissionsModule?: boolean;
  canViewDashboard?: boolean;
  canViewInactiveUsers?: boolean;
  perms: Set<string>;
}) {
  const can = (p: string) => isGlobalAdmin || perms.has(p);
  // Track which sections are expanded - default to expanding the active section
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    NAV_SECTIONS.forEach((section) => {
      // Auto-expand the section if the current path matches
      initial[section.label] = pathname.startsWith(section.href);
    });
    return initial;
  });

  const handleSectionToggle = (label: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo and Branding */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          px: 2,
          py: 2.5,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Image
            src="/origin-north-horizontal-2.png"
            alt="Origin-North"
            width={140}
            height={56}
            style={{ width: "140px", height: "auto", display: "block" }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.35, letterSpacing: "0.02em" }}
        >
          IAM Platform Console
        </Typography>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", py: 1 }}>
        <List component="nav" disablePadding>
          {(() => {
            // Build sections dynamically, gating each by the caller's permissions
            let sections = NAV_SECTIONS.map((section) => {
              if (section.label === "Dashboard") {
                return {
                  ...section,
                  items: section.items.filter((item) => {
                    if (item.href === "/dashboard/ecosystem-users")
                      return can("user:read");
                    if (item.href === "/inactive-report")
                      return canViewInactiveUsers;
                    if (item.href === "/dashboard/api-keys")
                      return can("audit:read");
                    if (item.href === "/dashboard/reports/user-activity")
                      return can("audit:read");
                    if (item.href === "/dashboard/inactivity-reviews")
                      return can("audit:read");
                    return true;
                  }),
                };
              }
              if (section.label === "Users") {
                return {
                  ...section,
                  items: section.items.filter((item) => {
                    if (item.href === "/users") return can("user:read");
                    if (item.href === "/users/invite")
                      return can("user:create");
                    if (item.href === "/users/api-keys")
                      return can("user:read");
                    return true;
                  }),
                };
              }
              if (section.label === "Tenant Data Sync") {
                return {
                  ...section,
                  items: section.items.filter((item) => {
                    if (item.href === "/data-sync/tenants") {
                      return can("data:tenantview");
                    }
                    if (item.href === "/data-sync/enterprise") {
                      return can("data:enterpriseview");
                    }
                    if (item.href === "/data-sync") {
                      return can("data:relationshipview");
                    }
                    if (item.href === "/data-sync/scheduler") {
                      return can("data:schedulerview");
                    }
                    return true;
                  }),
                };
              }
              if (section.label === "Access Control") {
                return {
                  ...section,
                  items: section.items.filter((item) => {
                    if (item.href === "/access/entities")
                      return can("entity:read");
                    if (item.href === "/access/roles") return can("role:read");
                    if (item.href === "/access/audit") return can("audit:read");
                    if (item.href === "/access/sessions")
                      return can("session:read");
                    return true;
                  }),
                };
              }
              return section;
            });
            // Remove sections whose items are all filtered out
            sections = sections.filter((s) => s.items.length > 0);
            // Top-level section gates
            if (!canViewDashboard) {
              sections = sections.filter((s) => s.label !== "Dashboard");
            }
            if (!can("user:read") && !can("user:create")) {
              // Keep a minimal Users section so users can always access their own profile
              const usersIndex = sections.findIndex((s) => s.label === "Users");
              if (usersIndex >= 0) {
                sections[usersIndex] = {
                  ...sections[usersIndex],
                  items: [
                    {
                      label: "Profile",
                      href: "/profile",
                      description: "View and edit your account",
                    },
                  ],
                };
              }
            }
            if (!can("user:read")) {
              sections = sections.filter((s) => s.label !== "Hierarchy");
            }
            if (!can("role:read")) {
              sections = sections.filter((s) => s.label !== "Roles");
            }
            if (canViewPermissionsModule) {
              const permissionsSection: NavSection = {
                label: "Permissions",
                href: "/permissions",
                icon: <ShieldOutlinedIcon fontSize="small" />,
                items: [
                  {
                    label: "Permission Catalog",
                    href: "/permissions",
                    description: "Create and manage permissions",
                  },
                ],
              };
              // insert after Roles (find index)
              const rolesIndex = sections.findIndex((s) => s.label === "Roles");
              if (rolesIndex >= 0)
                sections.splice(rolesIndex + 1, 0, permissionsSection);
              else sections.push(permissionsSection);
            }

            sections.sort((left, right) => {
              const leftIndex = NAV_SECTION_ORDER.indexOf(left.label);
              const rightIndex = NAV_SECTION_ORDER.indexOf(right.label);
              return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex) -
                (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
            });

            return sections.map((section) => {
              const isActive = pathname.startsWith(section.href);
              const isExpanded = expandedSections[section.label];

              return (
                <Box key={section.label} sx={{ mb: 0.5 }}>
                  {/* Section Header - Collapsible */}
                  <ListItemButton
                    onClick={() => handleSectionToggle(section.label)}
                    sx={{
                      py: 1.5,
                      px: 2.5,
                      mx: 1,
                      borderRadius: 2,
                      backgroundColor: isActive
                        ? "rgba(76, 154, 255, 0.08)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(76, 154, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isActive ? "primary.main" : "text.secondary",
                      }}
                    >
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={isActive ? 700 : 600}
                          color={isActive ? "primary.main" : "text.primary"}
                        >
                          {section.label}
                        </Typography>
                      }
                    />
                    {isExpanded ? (
                      <ExpandLess
                        sx={{
                          color: isActive ? "primary.main" : "text.secondary",
                          fontSize: 20,
                        }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{
                          color: isActive ? "primary.main" : "text.secondary",
                          fontSize: 20,
                        }}
                      />
                    )}
                  </ListItemButton>

                  {/* Submenu Items - Collapsible */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {section.items.map((item) => {
                        const isItemActive =
                          item.href === "/data-sync" ||
                          item.href === "/dashboard"
                            ? pathname === item.href
                            : pathname === item.href ||
                              pathname.startsWith(item.href + "/");

                        return (
                          <ListItemButton
                            key={item.label}
                            component={Link}
                            href={item.href}
                            selected={isItemActive}
                            sx={{
                              pl: 7,
                              pr: 2.5,
                              py: 1,
                              mx: 1,
                              borderRadius: 2,
                              mb: 0.25,
                              "&.Mui-selected": {
                                backgroundColor: "rgba(76, 154, 255, 0.12)",
                                borderLeft: "3px solid",
                                borderColor: "primary.main",
                                "&:hover": {
                                  backgroundColor: "rgba(76, 154, 255, 0.18)",
                                },
                              },
                            }}
                            onClick={onNavigate}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  fontWeight={isItemActive ? 600 : 400}
                                  color={
                                    isItemActive
                                      ? "primary.main"
                                      : "text.primary"
                                  }
                                >
                                  {item.label}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            });
          })()}
        </List>
      </Box>

      <Divider />

      {/* Footer Note */}
      <Box sx={{ px: 3, py: 2 }} />
    </Box>
  );
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || "/";
  const [mobileOpen, setMobileOpen] = useState(false);
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const {
    user,
    loading: authLoading,
    accessToken,
    actorPermissions,
  } = useAuth();

  const handleMenuToggle = () => setMobileOpen((open) => !open);

  const handleNavigate = useCallback(() => {
    if (!lgUp) {
      setMobileOpen(false);
    }
  }, [lgUp]);

  const isGlobalAdmin = useMemo(() => {
    try {
      if (user && (user as any).isSystemAdmin) return true;
      const token = accessToken;
      if (typeof token === "string" && token.split(".").length === 3) {
        try {
          const payloadB64 = token
            .split(".")[1]
            .replace(/-/g, "+")
            .replace(/_/g, "/");
          const json = JSON.parse(decodeURIComponent(escape(atob(payloadB64))));
          const roleId = (json.roleId || json.role_id || json.role || "")
            .toString()
            .toLowerCase();
          const roleName = (json.name || json.role || "")
            .toString()
            .toLowerCase();
          const envList =
            process.env.NEXT_PUBLIC_SYSTEM_ADMIN_ROLE_IDS ||
            process.env.NEXT_PUBLIC_SYSTEM_ADMIN_ROLE_ID ||
            "SYSADM,ENTSYSADM";
          const adminIds = new Set(
            String(envList)
              .split(",")
              .map((s) =>
                String(s || "")
                  .trim()
                  .toLowerCase(),
              )
              .filter(Boolean),
          );
          if (adminIds.has(roleId) || adminIds.has(roleName)) return true;
          if (
            roleName.includes("system") ||
            roleName.includes("admin") ||
            roleName.includes("administrator")
          )
            return true;
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }
    return false;
  }, [accessToken, user]);

  const canViewPermissionsModule = useMemo(() => {
    if (isGlobalAdmin) return true;
    const perms = actorPermissions || [];
    return perms.includes("permission:read");
  }, [actorPermissions, isGlobalAdmin]);

  const canViewDashboard = useMemo(() => {
    if (isGlobalAdmin) return true;
    return (actorPermissions || []).includes("iam:dashboard");
  }, [actorPermissions, isGlobalAdmin]);

  const canViewInactiveUsers = useMemo(() => {
    if (isGlobalAdmin) return true;
    return (actorPermissions || []).includes("iam:inactiveusers");
  }, [actorPermissions, isGlobalAdmin]);

  const permsSet = useMemo(
    () => new Set<string>(actorPermissions || []),
    [actorPermissions],
  );

  const drawer = useMemo(
    () => (
      <DrawerContent
        pathname={pathname}
        onNavigate={handleNavigate}
        isGlobalAdmin={isGlobalAdmin}
        canViewPermissionsModule={canViewPermissionsModule}
        canViewDashboard={canViewDashboard}
        canViewInactiveUsers={canViewInactiveUsers}
        perms={permsSet}
      />
    ),
    [
      pathname,
      handleNavigate,
      isGlobalAdmin,
      canViewPermissionsModule,
      canViewDashboard,
      canViewInactiveUsers,
      permsSet,
    ],
  );
  // Keep the primary navigation mounted even when the session has expired
  // so the menu doesn't disappear abruptly. We still avoid showing it while
  // auth is loading to prevent flicker during initial sign-in.
  const showNavigation = !authLoading;

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      <Header onMenuClick={handleMenuToggle} drawerWidth={drawerWidth} />

      <Box
        component="nav"
        sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
        aria-label="Primary navigation"
      >
        {showNavigation ? (
          <Drawer
            variant={lgUp ? "permanent" : "temporary"}
            open={lgUp ? true : mobileOpen}
            onClose={handleMenuToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
                borderRight: "none",
                backgroundColor: "#ffffff",
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          // show a slim placeholder with sign-in CTA on mobile/guest views
          <Box sx={{ width: { lg: 0 }, px: 2, py: 2 }}>
            {/* keep layout stable when navigation hidden */}
          </Box>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Toolbar sx={{ height: "88px" }} />
        <Box sx={{ flexGrow: 1, px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
